// client/src/components/admin/AdminEmailOutbox.js
import React from 'react'
import { useParams } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import useAuth from '../../hooks/useAuth'
import useEmailOutboxData from './useEmailOutboxData'
import { getCurrentCycleKey } from './emailOutboxUtils'
import EmailOutboxFilters from './EmailOutboxFilters'
import EmailOutboxBatchActions from './EmailOutboxBatchActions'
import EmailOutboxTable from './EmailOutboxTable'
import EmailOutboxPagination from './EmailOutboxPagination'
import EmailOutboxPreviewModal from './EmailOutboxPreviewModal'
import EmailOutboxTestSendModal from './EmailOutboxTestSendModal'
import NotificationEmailActivityPanel from './NotificationEmailActivityPanel'
import './AdminEmailOutbox.css'

function AdminEmailOutbox() {
  const { leagueId: leagueIdParam } = useParams()
  const { isAdmin } = useAuth()

  const {
    leagueId,
    leagues,
    selectedLeagueId,
    setSelectedLeagueId,
    leagueName,
    drafts,
    loading,
    generating,
    lastUpdated,
    statusFilter,
    setStatusFilter,
    templateFilter,
    setTemplateFilter,
    cycleFilter,
    setCycleFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchTerm,
    setSearchTerm,
    sortColumn,
    sortDirection,
    handleSort,
    currentPage,
    setCurrentPage,
    totalDrafts,
    totalPages,
    startIndex,
    endIndex,
    paginatedDrafts,
    uniqueCycles,
    draftCount,
    failedCount,
    showPreviewModal,
    setShowPreviewModal,
    previewDraft,
    previewTab,
    setPreviewTab,
    showHelp,
    setShowHelp,
    showTestSendModal,
    setShowTestSendModal,
    testSendDraft,
    testEmail,
    setTestEmail,
    sendingTest,
    sending,
    canceling,
    retrying,
    handleGenerateDrafts,
    handleRefresh,
    handlePreview,
    handleTestSend,
    confirmTestSend,
    handleSend,
    handleCancel,
    handleRetry,
    handleSendAllDrafts,
    handleCancelAllDrafts,
    handleRetryFailed,
  } = useEmailOutboxData({ isAdmin, leagueIdParam })

  if (loading && drafts.length === 0) {
    return (
      <PageLayout title="Email Outbox" noCard={true}>
        <div>Loading...</div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title={`Email Outbox${leagueName ? ` - ${leagueName}` : ''}`}
      noCard={true}
    >
      {!leagueIdParam && (
        <div className="mb-3">
          <label className="form-label">Select League</label>
          <select
            className="form-select"
            value={selectedLeagueId}
            onChange={(e) => setSelectedLeagueId(e.target.value)}
          >
            <option value="">-- Select a League --</option>
            {leagues.map((league) => (
              <option key={league.id} value={league.id}>
                {league.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {!leagueId && (
        <div className="alert alert-info">
          Please select a league to view email drafts.
        </div>
      )}

      {leagueId && (
        <>
          <div className="mb-3">
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => setShowHelp(!showHelp)}
              aria-expanded={showHelp}
            >
              {showHelp ? '▼' : '▶'} What are these email types?
            </button>
            {showHelp && (
              <div className="alert alert-info mt-2">
                <h6 className="fw-bold">Email Types Explanation</h6>
                <p className="mb-2">
                  These automated emails nudge players through the scheduling
                  process each week:
                </p>
                <ul className="mb-2">
                  <li>
                    <strong className="text-danger">
                      Availability Missing:
                    </strong>{' '}
                    <em>Step 1:</em> Remind players who haven't marked their
                    free times to set their availability.
                  </li>
                  <li>
                    <strong className="text-warning">Response Needed:</strong>{' '}
                    <em>Step 2:</em> Prompt players who received match proposals
                    to accept or decline.
                  </li>
                  <li>
                    <strong className="text-info">Ready, No Proposal:</strong>{' '}
                    <em>Step 3:</em> Encourage available players to propose
                    matches with opponents.
                  </li>
                </ul>
                <p className="mb-0 small text-muted">
                  <strong>Note:</strong> Players only receive the email for
                  their current step in the process.
                </p>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Email Outbox for {leagueName}</h4>
            <div>
              <button
                onClick={handleGenerateDrafts}
                disabled={generating}
                className="btn btn-primary"
              >
                {generating ? 'Generating...' : '🔄 Generate Drafts'}
              </button>
              <button
                onClick={handleRefresh}
                className="btn btn-secondary ms-2"
              >
                🔄 Refresh
              </button>
            </div>
          </div>

          <div className="email-outbox-meta mb-3">
            <div className="text-muted small">
              Last updated: {lastUpdated?.toLocaleString() || 'Never'}
            </div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <span className="badge text-bg-light">
                Loaded: {drafts.length}
              </span>
              <span className="badge text-bg-secondary">
                Filtered: {totalDrafts}
              </span>
              <span className="badge text-bg-primary">Draft: {draftCount}</span>
              <span className="badge text-bg-danger">
                Failed: {failedCount}
              </span>
            </div>
          </div>

          <EmailOutboxFilters
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            templateFilter={templateFilter}
            setTemplateFilter={setTemplateFilter}
            cycleFilter={cycleFilter}
            setCycleFilter={setCycleFilter}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            uniqueCycles={uniqueCycles}
          />

          <EmailOutboxBatchActions
            draftCount={draftCount}
            failedCount={failedCount}
            sending={sending}
            canceling={canceling}
            retrying={retrying}
            onSendAll={handleSendAllDrafts}
            onCancelAll={handleCancelAllDrafts}
            onRetryFailed={handleRetryFailed}
          />

          <EmailOutboxTable
            drafts={paginatedDrafts}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            onPreview={handlePreview}
            onTestSend={handleTestSend}
            onSend={handleSend}
            onCancel={handleCancel}
            onRetry={handleRetry}
          />

          {paginatedDrafts.length > 0 && (
            <EmailOutboxPagination
              startIndex={startIndex}
              endIndex={endIndex}
              totalDrafts={totalDrafts}
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          )}

          <EmailOutboxPreviewModal
            show={showPreviewModal}
            onHide={() => setShowPreviewModal(false)}
            draft={previewDraft}
            previewTab={previewTab}
            setPreviewTab={setPreviewTab}
            onOpenTestSend={handleTestSend}
          />

          <EmailOutboxTestSendModal
            show={showTestSendModal}
            onHide={() => setShowTestSendModal(false)}
            testSendDraft={testSendDraft}
            testEmail={testEmail}
            setTestEmail={setTestEmail}
            onConfirm={confirmTestSend}
            sendingTest={sendingTest}
          />

          <NotificationEmailActivityPanel />
        </>
      )}
    </PageLayout>
  )
}

export { getCurrentCycleKey }
export default AdminEmailOutbox
